import { useCallback, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getStoredUtmParams } from "@/lib/utmTracking";

// Conversion funnel event types
export type ConversionEventType = 
  | "page_view"
  | "registration_started"
  | "registration_completed"
  | "profile_created"
  | "profile_completed"
  | "subscription_page_viewed"
  | "subscription_selected"
  | "checkout_started"
  | "payment_completed"
  | "job_posting_created"
  | "job_posting_response"
  | "message_sent"
  | "profile_view"
  | "search_performed"
  | "affiliate_link_generated"
  | "recommendation_created";

export type EventCategory = 
  | "acquisition"
  | "activation"
  | "retention"
  | "revenue"
  | "referral";

// Behavior event types
export type BehaviorEventType =
  | "click"
  | "scroll"
  | "form_interaction"
  | "time_on_page"
  | "feature_usage"
  | "navigation"
  | "modal_opened"
  | "modal_closed"
  | "button_clicked"
  | "link_clicked";

// Get or create anonymous ID
const getAnonymousId = (): string => {
  let anonId = localStorage.getItem("pzm_anon_id");
  if (!anonId) {
    anonId = `anon_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    localStorage.setItem("pzm_anon_id", anonId);
  }
  return anonId;
};

// Get or create session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem("pzm_session_id");
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem("pzm_session_id", sessionId);
  }
  return sessionId;
};

// Get first-touch UTM params from localStorage (never from current URL)
const getUtmParams = () => {
  const stored = getStoredUtmParams();
  return {
    utm_source: stored.utm_source,
    utm_medium: stored.utm_medium,
    utm_campaign: stored.utm_campaign,
    utm_content: stored.utm_content,
    utm_term: stored.utm_term,
    gclid: stored.gclid,
  };
};

// Get device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "desktop";
};

// Get browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Chrome") && !ua.includes("Edg")) return "Chrome";
  if (ua.includes("Safari") && !ua.includes("Chrome")) return "Safari";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  return "Other";
};

export const useConversionTracking = () => {
  const { session } = useAuth();
  const pageLoadTime = useRef(Date.now());

  // Track conversion event
  const trackConversion = useCallback(async (
    eventType: ConversionEventType,
    eventCategory: EventCategory,
    eventData?: Record<string, unknown>
  ) => {
    try {
      const utmParams = getUtmParams();
      
      // Use any type to bypass TypeScript check since table was just created
      await (supabase as any).from("conversion_events").insert({
        user_id: session?.user?.id || null,
        anonymous_id: getAnonymousId(),
        event_type: eventType,
        event_category: eventCategory,
        event_data: { ...(eventData || {}), ...(utmParams.gclid ? { gclid: utmParams.gclid } : {}) },
        page_url: window.location.href,
        referrer_url: document.referrer || null,
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_content: utmParams.utm_content,
        utm_term: utmParams.utm_term,
        device_type: getDeviceType(),
        browser: getBrowser(),
      });
    } catch (error) {
      console.error("Failed to track conversion event:", error);
    }
  }, [session?.user?.id]);

  // Track behavior event
  const trackBehavior = useCallback(async (
    eventType: BehaviorEventType,
    elementDetails?: {
      id?: string;
      className?: string;
      text?: string;
    },
    eventData?: Record<string, unknown>
  ) => {
    try {
      // Use any type to bypass TypeScript check since table was just created
      await (supabase as any).from("user_behavior_events").insert({
        user_id: session?.user?.id || null,
        anonymous_id: getAnonymousId(),
        session_id: getSessionId(),
        event_type: eventType,
        element_id: elementDetails?.id || null,
        element_class: elementDetails?.className || null,
        element_text: elementDetails?.text?.substring(0, 100) || null,
        page_url: window.location.href,
        event_data: eventData || {},
      });
    } catch (error) {
      console.error("Failed to track behavior event:", error);
    }
  }, [session?.user?.id]);

  // Track page view automatically
  const trackPageView = useCallback(() => {
    trackConversion("page_view", "acquisition", {
      title: document.title,
      path: window.location.pathname,
    });
  }, [trackConversion]);

  // Track time on page when leaving
  const trackTimeOnPage = useCallback(() => {
    const timeSpent = Math.floor((Date.now() - pageLoadTime.current) / 1000);
    if (timeSpent > 2) { // Only track if more than 2 seconds
      trackBehavior("time_on_page", undefined, {
        seconds: timeSpent,
        path: window.location.pathname,
      });
    }
  }, [trackBehavior]);

  // Track scroll depth
  const trackScrollDepth = useCallback((depth: number) => {
    // Use any type to bypass TypeScript check
    (supabase as any).from("user_behavior_events").insert({
      user_id: session?.user?.id || null,
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      event_type: "scroll",
      page_url: window.location.href,
      scroll_depth: depth,
    }).catch((error: Error) => console.error("Failed to track scroll:", error));
  }, [session?.user?.id]);

  return {
    trackConversion,
    trackBehavior,
    trackPageView,
    trackTimeOnPage,
    trackScrollDepth,
    getAnonymousId,
    getSessionId,
  };
};

// Standalone tracking function for use outside React components
export const trackConversionEvent = async (
  eventType: ConversionEventType,
  eventCategory: EventCategory,
  eventData?: Record<string, unknown>,
  userId?: string
) => {
  try {
    const utmParams = getUtmParams();
    
    await (supabase as any).from("conversion_events").insert({
      user_id: userId || null,
      anonymous_id: getAnonymousId(),
      event_type: eventType,
      event_category: eventCategory,
      event_data: { ...(eventData || {}), ...(utmParams.gclid ? { gclid: utmParams.gclid } : {}) },
      page_url: window.location.href,
      referrer_url: document.referrer || null,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
      utm_term: utmParams.utm_term,
      device_type: getDeviceType(),
      browser: getBrowser(),
    });
  } catch (error) {
    console.error("Failed to track conversion event:", error);
  }
};
